import { type ChainId, type MultichainTokenBalance } from "@umami/chains";
import {
  Contract,
  type JsonRpcProvider,
  MaxUint256,
  type TransactionRequest,
  type TransactionResponse,
  type Wallet,
  concat,
  getBytes,
  toBeHex,
} from "ethers";

import { type BridgeNetwork, CHAIN_ID_MAPPING } from "../config";
import { getFirmQuote } from "./firmQuote";
import { executeGaslessSwap } from "./gaslessSwap";
import { tokenAmountToWei } from "../utils/token-conversion";

export class SwapExecutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SwapExecutionError";
  }
}

export const PERMIT2_ADDRESS = "0x000000000022D473030F116dDEE9F6B43aC78BA3";
export const ZEROX_ETH_ADDRESS = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";
export const NATIVE_ETH_ADDRESS = "0x0000000000000000000000000000000000000000";

const erc20Abi = [
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address account) view returns (uint256)",
  "function symbol() view returns (string)",
  "function name() view returns (string)",
];

export interface ExecuteSwapParams {
  sellToken: Omit<MultichainTokenBalance, "chainId"> & { chainId: ChainId };
  buyToken: Omit<MultichainTokenBalance, "chainId"> & { chainId: ChainId };
  sellAmount: string;
  buyAmount: string;
  signer: Wallet;
  provider: JsonRpcProvider;
  network: BridgeNetwork;
  gasless?: boolean;
}

export async function executeSwap({
  sellToken,
  buyToken,
  sellAmount,
  buyAmount,
  signer,
  provider,
  network,
  gasless = false,
}: ExecuteSwapParams): Promise<TransactionResponse> {
  try {
    // If gasless swap is requested, use the gasless implementation
    if (gasless) {
      const sellAmountWei = tokenAmountToWei(sellAmount, sellToken.decimals);
      const result = await executeGaslessSwap(
        sellToken.address || "",
        buyToken.address || "",
        sellAmountWei,
        signer,
        CHAIN_ID_MAPPING[sellToken.chainId as ChainId]
      );

      // Create a mock TransactionResponse for gasless swaps
      const mockResponse = {
        hash: result.tradeHash,
        to: null,
        from: signer.address,
        data: "",
        value: 0n,
        nonce: 0,
        gasLimit: 0n,
        gasPrice: 0n,
        chainId: 1n,
        type: 0,
        confirmations: 0,
        blockNumber: null,
        blockHash: null,
        index: 0,
        wait: () => ({ status: 1, logs: [] }),
      } as unknown as TransactionResponse;

      return mockResponse;
    }

    const quote = await getFirmQuote({
      sellToken,
      buyToken,
      sellAmount,
      buyAmount,
      takerAddress: signer.address,
      network,
      gasless: false,
    });

    if (!quote) {
      throw new SwapExecutionError("Failed to get swap quote");
    }

    if (!quote.liquidityAvailable) {
      throw new SwapExecutionError("No liquidity available for this swap");
    }

    const isNativeEth =
      sellToken.address === NATIVE_ETH_ADDRESS ||
      sellToken.address === ZEROX_ETH_ADDRESS ||
      sellToken.symbol === "ETH";

    // Handle approval for ERC20 tokens (skip for native ETH)
    if (!isNativeEth) {
      // Only perform approval if allowance data is available in the quote
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (quote.issues?.allowance?.spender) {
        const tokenContract = new Contract(sellToken.address!, erc20Abi, signer);

        try {
          const approveTx = await tokenContract.approve(quote.issues.allowance.spender, MaxUint256);
          const approveReceipt = await approveTx.wait();

          // Verify the approval transaction was successful
          if (!approveReceipt?.status) {
            throw new SwapExecutionError("Approval transaction failed");
          }
        } catch (error) {
          console.error("Error approving Permit2:", error);
          throw new SwapExecutionError(
            `Failed to approve token: ${error instanceof Error ? error.message : "Unknown error"}`
          );
        }
      }
    }

    if (!quote.transaction) {
      throw new SwapExecutionError("No transaction data in quote");
    }

    const { transaction } = quote;

    // Handle permit2 signature for ERC20 tokens
    let finalTransactionData = transaction.data;

    if (quote.permit2?.eip712 && !isNativeEth) {
      try {
        // Create a types object with only the primary type and its dependencies
        const primaryType = quote.permit2.eip712.primaryType;
        const types = {
          [primaryType]: quote.permit2.eip712.types[primaryType],
          TokenPermissions: quote.permit2.eip712.types.TokenPermissions,
        };

        // Sign the EIP-712 permit2 message
        const signature = await signer.signTypedData(
          quote.permit2.eip712.domain,
          types,
          quote.permit2.eip712.message
        );

        const signatureBytes = getBytes(signature);
        const signatureLengthHex = toBeHex(signatureBytes.length, 32);

        finalTransactionData = concat([transaction.data, signatureLengthHex, signature]);
      } catch (error) {
        console.error("Error signing permit2 message:", error);
        throw new SwapExecutionError(
          `Failed to sign permit2 message: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    }

    const nonce = await provider.getTransactionCount(signer.address);

    const txRequest: TransactionRequest = {
      to: transaction.to,
      data: finalTransactionData,
      value: BigInt(transaction.value),
      gasLimit: transaction.gas ? BigInt(transaction.gas) : undefined,
      gasPrice: transaction.gasPrice ? BigInt(transaction.gasPrice) : undefined,
      nonce,
    };

    const txResponse = await signer.sendTransaction(txRequest);

    const receipt = await txResponse.wait();

    if (!receipt?.status) {
      throw new SwapExecutionError("Transaction failed");
    }

    return txResponse;
  } catch (error) {
    console.error("Error executing swap:", error);

    if (error instanceof SwapExecutionError) {
      throw error;
    }

    throw new SwapExecutionError(
      `Swap execution failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
